import React, {useCallback, useMemo, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery} from 'react-query';
import CustomersWrapper from './CustomersWrapper';
import {SearchInput} from 'components';
import CardSummary from 'components/CardSummary';
import {baasBalance, green, red} from 'assets/images/images';
import {useSelector} from 'react-redux';
import BaasTransactionModal from '../../BaasTransactionModal';
import BaasTransactionTable from '../../components/BaasTransactionTable';
import {
  fetchBAASTransactions,
  fetchBAASTransactionsSummary,
} from 'requests/queries/baas';
import {typeAndStatusFilterOptions} from '../../constants';
import {shortenLargeNumbers} from 'helpers/shortenLargeNumbers';
import AppliedFilters from 'components/AppliedFilters';

const initialFilters = {
  start: '',
  end: '',
};

function CustomerTransactions() {
  const navigate = useNavigate();
  const {slug} = useParams();
  const [transactionDetails, setTransactionDetails] = useState(false);
  const [transaction, setTransaction] = useState({});
  const [searchText, setSearchText] = useState('');
  const {userDetails} = useSelector(state => state?.auth);
  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });
  const [filters, setFilters] = useState(initialFilters);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterProps, setFilterProps] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const toggleTransactionsModal = useCallback(transaction => {
    setTransactionDetails(prevState => !prevState);
    setTransaction(transaction);
  }, []);

  const params = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      ...query,
      search: searchText,
      customer_id: slug,
      start_date: filters?.start,
      end_date: filters?.end,
      status: filters?.transaction_status?.toString()?.toUpperCase(),
      transaction_type: filters?.transaction_type?.toString()?.toLowerCase(),
    }),
    [
      query,
      searchText,
      userDetails,
      slug,
      filters?.end,
      filters?.start,
      filters?.transaction_status,
      filters?.transaction_type,
    ],
  );

  const summaryParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      customer_id: slug,
      start_date: filters?.start,
      end_date: filters?.end,
      status: filters?.transaction_status?.toString()?.toUpperCase(),
      transaction_type: filters?.transaction_type?.toString()?.toLowerCase(),
    }),
    [
      userDetails,
      slug,
      filters?.start,
      filters?.end,
      filters?.transaction_status,
      filters?.transaction_type,
    ],
  );

  const {data: baas_transactions, isLoading} = useQuery(
    ['customer-transactions', {params}],
    () => fetchBAASTransactions({params}),
    {
      retry: false,
      meta: {preventGlobalError: true},
    },
  );

  const {data: summaryData, isLoading: summaryLoading} = useQuery(
    ['baas-transaction-summary', {summaryParams}],
    () => fetchBAASTransactionsSummary({params: summaryParams}),
  );

  const transactions = useMemo(
    () =>
      !isLoading &&
      baas_transactions &&
      baas_transactions?.entity?.transactions,
    [baas_transactions, isLoading],
  );

  const handlePageChange = page => {
    setQuery(prev => ({...prev, page}));
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', page);
    const newSearchParams = searchParams.toString();
    navigate(`?${newSearchParams}`);
  };

  const handleLimitChange = length => {
    setQuery(prev => ({...prev, length, page: 1}));
  };

  const handleResetFilter = () => {
    navigate('');
    setFilters(initialFilters);
    setOpenFilter(true);
    setSearchText('');
    setFiltersApplied(false);
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      Object.values(filters).some(value => value) || searchText.length > 0,
    );
  };

  const formattedSummary = useMemo(
    () => ({
      deposit: shortenLargeNumbers(summaryData?.deposit || 0, 'NGN', {
        startAt: 'B',
      }),
      transfer: shortenLargeNumbers(summaryData?.transfer || 0, 'NGN', {
        startAt: 'B',
      }),
      net: shortenLargeNumbers(summaryData?.net || 0, 'NGN', {startAt: 'B'}),
    }),
    [summaryData],
  );

  return (
    <CustomersWrapper>
      <div className="mt-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <h3 className="text-grey text-[18px] font-medium">Transactions</h3>
            {filtersApplied && filterProps && (
              <AppliedFilters {...filterProps} isMainPage={true} />
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <SearchInput onChange={value => setSearchText(value)} />
            <BaasTransactionTable.FilterButton
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </div>
        </div>
        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <CardSummary
            img={green}
            title="Total Collections"
            body={formattedSummary.deposit || '₦0'}
            className="!px-4 !py-10 bg-white80"
            loading={summaryLoading}
          />
          <CardSummary
            img={red}
            title="Total Payout"
            body={formattedSummary.transfer || '₦0'}
            className="!px-4 !py-10 bg-white80"
            loading={summaryLoading}
          />
          <CardSummary
            img={baasBalance}
            className="!px-4 !py-10 bg-white80"
            title="Estimated Settlements"
            body={formattedSummary.settlement || '₦0'}
            loading={summaryLoading}
          />
        </div>

        <BaasTransactionModal
          show={transactionDetails}
          onClose={toggleTransactionsModal}
          transaction={transaction}
        />

        <BaasTransactionTable
          transactions={transactions || []}
          isLoading={isLoading}
          customerId={slug}
          onViewTransaction={toggleTransactionsModal}
          className="mt-7"
          totalPages={baas_transactions?.entity?.total_pages || 1}
          currentPage={Number(baas_transactions?.entity?.current_page) || 1}
          onPageChange={handlePageChange}
          limit={query.length}
          onLimitChange={handleLimitChange}
          totalItems={baas_transactions?.entity?.current_page || 0}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          filters={filters}
          setFilters={setFilters}
          filterOptions={typeAndStatusFilterOptions}
          onResetFilter={handleResetFilter}
          navigate={navigate}
          searchText={searchText}
          setSearchText={setSearchText}
          initialFilters={initialFilters}
          onFilterStateChange={handleFilterStateChange}
        />
      </div>
    </CustomersWrapper>
  );
}

export default CustomerTransactions;
